/**
 * Contrat de figure — F1 de `docs/produit/SPEC-REPRESENTATION-VISUELLE.md`
 * (§3.2 à §3.4, §7.2, §7.3).
 *
 * Le modèle n'émet JAMAIS une image, ni du code, ni des coordonnées : il émet
 * une **spécification bornée** (une famille fermée + les données du problème).
 * Trois couches la refusent, dans cet ordre (§3.4) :
 *
 *   spec bornée (modèle) → Zod → figureCoherente() → resoudreFigure() → FigureResolue
 *                                                        ├─→ <Figure> SVG client
 *                                                        ├─→ tracer.py → PNG (courriel)
 *                                                        └─→ decrireFigure() (accessibilité)
 *
 * Deux règles de conception à ne jamais rouvrir :
 *
 *  1. **Aucune longueur dérivable n'est fournie par le modèle** (§3.1.2). Il
 *     émet `rayon: 7` ; le « 14 cm » du diamètre est CALCULÉ par le solveur.
 *     Une étiquette ne peut donc pas mentir sur la figure qu'elle annote.
 *  2. **La description textuelle n'est pas un champ, c'est une fonction**
 *     (§3.1.3, §10.6) — `decrireFigure()`. Si le modèle l'écrivait, elle
 *     pourrait contredire le dessin ; or pour un élève dyslexique et pour un
 *     lecteur d'écran, cette phrase EST l'énoncé.
 *
 * Bornes communes, alignées sur `packages/api/src/modules/graphes/spec.schema.ts` :
 * tout nombre est fini dans [-1000, 1000] ; toute chaîne libre est bornée en
 * longueur ; tout tableau est borné en cardinalité. Aucun champ n'accepte une
 * expression, une formule, une couleur, une unité de pixel, une URL ni du
 * balisage.
 *
 * Uniquement des formes de données ici. Le solveur pur et le générateur de
 * description vivent dans `packages/shared/src/figures/` (partagés client et
 * serveur) ; la validation Zod vit dans `packages/api/src/modules/figures/`.
 */
/* ------------------------------------------------------------ vocabulaire */
/** Les six familles fermées. `GRAPHE_FAMILLES` n'est PAS remplacé : il devient l'objet `courbe` de `repere` (§3.3.4). */
export const FIGURE_FAMILLES = [
    'triangle',
    'cercle',
    'polygone',
    'repere',
    'diagramme',
    /* Ajoutée au Sprint 32. Le programme de 3e la marque « étoile » : « aire de
       la sphère, aire latérale ou totale de cônes droits », « volume de prismes
       droits, de cylindres droits, de pyramides droites, de cônes droits et de
       boules », et « Représenter, dans le plan, des figures à trois
       dimensions » — cette famille même. */
    'solide',
];
export const UNITES_LONGUEUR = ['mm', 'cm', 'm', 'km', 'u'];
/** Teinte d'un trait ou d'une étiquette. Mappe les classes `.acc` / `.attn` de la maquette (§12.1). */
export const TONS_FIGURE = ['neutre', 'accent', 'attention'];
/** Style de trait. Mappe `.pointille` et `.fort`. */
export const STYLES_TRAIT = ['plein', 'pointille', 'fort'];
/* ---------------------------------------------------------------- solide */
export const SOLIDES_USUELS = [
    'cube',
    'prisme_droit',
    'cylindre',
    'pyramide',
    'cone',
    'sphere',
];
/** La base d'un prisme ou d'une pyramide — polygones que le programme nomme. */
export const BASES_SOLIDE = ['triangle', 'carre', 'rectangle', 'hexagone'];
/**
 * Ce qui est tracé EN APPUI sur le solide, en pointillé.
 *
 * L'apothème n'est jamais une donnée d'entrée : il se DÉDUIT de la hauteur et
 * de la base (règle 1 du contrat — aucune longueur dérivable n'est fournie par
 * le modèle). Le demander ici trace le segment ET calcule sa mesure.
 */
export const REPERES_SOLIDE = ['hauteur', 'rayon', 'apotheme', 'diagonale_base'];
/* --------------------------------------------------------------- triangle */
export const TRIANGLE_CONSTRUCTIONS = [
    'trois_cotes',
    'deux_cotes_angle',
    'cote_deux_angles',
    'rectangle',
];
/* --------------------------------------------------------------- polygone */
export const POLYGONES_USUELS = [
    'rectangle',
    'carre',
    'parallelogramme',
    'losange',
    'trapeze',
    'regulier',
];
export const MESURES_POLYGONE = ['base', 'cote', 'hauteur', 'angle'];
/* -------------------------------------------------------------- diagramme */
/** Trois types, pas quatre : la boîte à moustaches est coupée (§10.1, H13). */
export const DIAGRAMME_TYPES = ['bandes', 'ligne_brisee', 'nuage_de_points'];
/** Motif de refus, journalisé par la boucle de génération à 3 tentatives (§3.4). */
export const MOTIFS_REFUS_FIGURE = ['schema:zod-invalide', 'coherence', 'insoluble'];
/** Classe de trait de la maquette (§12.1) : `.ln`, `.ln-2`, `.grille`. */
export const TRAITS_FIGURE = ['principal', 'secondaire', 'grille'];
export const ALIGNEMENTS_TEXTE = ['debut', 'milieu', 'fin'];
/** Vocabulaire de prédicats fermé, évalué sur la figure RÉSOLUE (§7.3). */
export const PREDICATS_FIGURE = [
    'point_en',
    'point_sur_segment',
    'point_sur_cercle',
    'segment_perpendiculaire',
    'segment_parallele',
    'points_colineaires',
    'longueur_egale',
    'vecteur_egal',
    'courbe_egale',
];
/** Valeurs par défaut de `ToleranceFigureDto`, écrites une seule fois (§7.3). */
export const TOLERANCE_FIGURE_DEFAUT = {
    distance: 0.5,
    angleDeg: 5,
    relative: 0.05,
};
//# sourceMappingURL=figures.js.map